import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import type { ConnectionFormRegistry } from '../../resources/model/connectionForms'
import { EMPTY_CONNECTORS, type ConnectorRegistry } from '../../resources/model/connectors'
import { mutationView, type MutationResultLike } from '../../resources/model/mutation'
import type { ResourceSummary } from '../../resources/model/types'
import { accessConsoleAction, grantsOf } from './grants'
import { PLATFORM_GROUP, PLATFORM_KEY_TITLES, PLATFORM_ROLES, roleTitle, splitPermissionKey, SUPER_ROLE } from './roles'
import type {
  AccessIntentState,
  AccessMatrix,
  AccessVm,
  CapabilityRefVm,
  MyAccess,
  MyAccessVm,
  PermissionEntry,
  PermissionGroupVm,
  ResourceGapVm,
  RolesSummaryVm,
} from './types'

/** Вход vm экрана прав: результаты запросов и намерение — их выбирает binding. */
export interface AccessVmInput {
  intent: AccessIntentState
  access: QueryResultLike<MyAccess>
  resources: QueryResultLike<readonly ResourceSummary[]>
  granted: QueryResultLike<readonly string[]>
  matrix: QueryResultLike<AccessMatrix>
  /** Стартовый набор прав выбранной роли (RP-FB5); нет — кнопки подстановки нет. */
  defaults?: QueryResultLike<readonly string[]>
  forms: ConnectionFormRegistry
  /** Манифесты видов с сервера: право, которого у ресурса «нет вовсе», определяется по ним. */
  connectors?: ConnectorRegistry
  saveResources: MutationResultLike<unknown>
  savePermissions: MutationResultLike<unknown>
}

const EMPTY: readonly never[] = []

function viewOf<I, T>(pick: (input: I) => QueryResultLike<T>) {
  return createSelector(
    [
      (input: I) => pick(input).isUninitialized,
      (input: I) => pick(input).isLoading,
      (input: I) => pick(input).isError,
      (input: I) => pick(input).isSuccess,
      (input: I) => pick(input).error,
      (input: I) => pick(input).data,
    ],
    (isUninitialized, isLoading, isError, isSuccess, error, data): QueryView =>
      queryView(
        {
          isUninitialized,
          isLoading,
          isError,
          isSuccess,
          ...(error !== undefined ? { error } : {}),
          ...(data !== undefined ? { data } : {}),
        },
        () => false,
      ),
  )
}

function savedOf(pick: (input: AccessVmInput) => MutationResultLike<unknown>) {
  return createSelector(
    [
      (input: AccessVmInput) => pick(input).isLoading,
      (input: AccessVmInput) => pick(input).isError,
      (input: AccessVmInput) => pick(input).isSuccess,
      (input: AccessVmInput) => pick(input).error,
    ],
    (isLoading, isError, isSuccess, error) => ({
      ...mutationView({ isLoading, isError, isSuccess, ...(error !== undefined ? { error } : {}) }),
      done: isSuccess,
    }),
  )
}

/**
 * Подпись права ресурса словами из пакета его вида (RP-FB3): ключ `onec:catalog:read` — право `catalog.read` вида
 * `onec`, «Чтение товаров». Сервер подписывает такие права кодом («onec: catalog.read»); подписи нет — остаётся его.
 */
function titleOf(entry: PermissionEntry, forms: ConnectionFormRegistry): string {
  if (entry.group === null || !entry.key.startsWith(`${entry.group}:`)) return entry.title
  const capability = entry.key.slice(entry.group.length + 1).replaceAll(':', '.')
  const label = forms.capabilityLabelOf(entry.group, capability)
  return label === capability ? entry.title : label
}

/** Подпись группы прав: вид ресурса словами, свои права Platform — «Платформа». */
function groupTitleOf(group: string, forms: ConnectionFormRegistry): string {
  if (group === 'other') return 'Прочее'
  if (group === PLATFORM_GROUP) return 'Платформа'
  return forms.titleOf(group)
}

/** Группы каталога прав по виду ресурса: «1С:Предприятие» — его права. */
function groupsOf(matrix: AccessMatrix | undefined, forms: ConnectionFormRegistry): readonly PermissionGroupVm[] {
  if (matrix === undefined) return EMPTY
  const groups = new Map<string, { key: string; title: string }[]>()
  for (const entry of matrix.catalog) {
    const group = entry.group ?? 'other'
    const items = groups.get(group) ?? []
    items.push({ key: entry.key, title: titleOf(entry, forms) })
    groups.set(group, items)
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([group, items]) => ({
      group,
      title: groupTitleOf(group, forms),
      items: items.sort((left, right) => left.key.localeCompare(right.key)),
    }))
}

/** Право словами по одному ключу: Platform — своим текстом, вид ресурса — подписью из его пакета, иначе код права. */
function keyTitleOf(key: string, forms: ConnectionFormRegistry): string {
  const platform = PLATFORM_KEY_TITLES[key]
  if (platform !== undefined) return platform
  const { group, capability } = splitPermissionKey(key)
  return group === 'other' ? key : forms.capabilityLabelOf(group, capability)
}

function ownGroupsOf(api: readonly string[], forms: ConnectionFormRegistry): readonly PermissionGroupVm[] {
  const groups = new Map<string, { key: string; title: string }[]>()
  for (const key of api) {
    const { group } = splitPermissionKey(key)
    const items = groups.get(group) ?? []
    items.push({ key, title: keyTitleOf(key, forms) })
    groups.set(group, items)
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([group, items]) => ({
      group,
      title: groupTitleOf(group, forms),
      items: items.sort((left, right) => left.key.localeCompare(right.key)),
    }))
}

function capabilityRef(forms: ConnectionFormRegistry, kind: string, code: string): CapabilityRefVm {
  return { code, label: forms.capabilityLabelOf(kind, code) }
}

/**
 * Ресурсы, у которых права вида не включены. «Нет вовсе» — право есть в манифесте вида (его отдаёт сервер), а в
 * списке прав ресурса его нет: вид добавил его позже, чем ресурс создан, и сервер считает такое право невключённым.
 */
function gapsOf(
  resources: readonly ResourceSummary[] | undefined,
  forms: ConnectionFormRegistry,
  connectors: ConnectorRegistry,
): readonly ResourceGapVm[] {
  if (resources === undefined) return EMPTY
  const gaps: ResourceGapVm[] = []
  for (const resource of resources) {
    const known = new Set(resource.capabilities.map((capability) => capability.name))
    const absent = connectors
      .capabilitiesOf(resource.kind)
      .filter((code) => !known.has(code))
      .map((code) => capabilityRef(forms, resource.kind, code))
    const disabled = resource.capabilities
      .filter((capability) => !capability.enabled)
      .map((capability) => capabilityRef(forms, resource.kind, capability.name))
    if (resource.enabled && absent.length === 0 && disabled.length === 0) continue
    gaps.push({
      id: resource.id,
      name: resource.displayName,
      kindTitle: forms.titleOf(resource.kind),
      resourceEnabled: resource.enabled,
      absent: resource.enabled ? absent : EMPTY,
      disabled: resource.enabled ? disabled : EMPTY,
    })
  }
  return gaps.length === 0 ? EMPTY : gaps
}

export const makeSelectAccessVm = () => {
  const selectResourcesView = viewOf<AccessVmInput, readonly ResourceSummary[]>((input) => input.resources)
  const selectGrantedView = viewOf<AccessVmInput, readonly string[]>((input) => input.granted)
  const selectMatrixView = viewOf<AccessVmInput, AccessMatrix>((input) => input.matrix)
  const selectManage = createSelector(
    [(input: AccessVmInput) => input.access.data, (input: AccessVmInput) => input.access.isError],
    (data, isError) =>
      accessConsoleAction(
        grantsOf({ isUninitialized: false, isLoading: false, isSuccess: !isError, isError, ...(data ? { data } : {}) }),
      ),
  )
  const selectRows = createSelector(
    [(input: AccessVmInput) => input.resources.data, (input: AccessVmInput) => input.forms],
    (resources, forms) =>
      (resources ?? EMPTY).map((resource) => ({
        id: resource.id,
        name: resource.displayName,
        kindTitle: forms.titleOf(resource.kind),
        enabled: resource.enabled,
      })),
  )
  const selectGroups = createSelector(
    [(input: AccessVmInput) => input.matrix.data, (input: AccessVmInput) => input.forms],
    groupsOf,
  )
  const selectRolePermissions = createSelector(
    [(input: AccessVmInput) => input.matrix.data, (input: AccessVmInput) => input.intent.role],
    (matrix, role) => matrix?.roles.find((entry) => entry.role === role)?.permissions ?? EMPTY,
  )
  const selectHidden = createSelector(
    [(input: AccessVmInput) => input.granted.data, (input: AccessVmInput) => input.resources.data],
    (granted, resources) => {
      if (granted === undefined || resources === undefined) return EMPTY
      const visible = new Set(resources.map((resource) => resource.id))
      const hidden = granted.filter((id) => !visible.has(id))
      return hidden.length === 0 ? EMPTY : hidden
    },
  )
  const selectAccessView = viewOf<AccessVmInput, MyAccess>((input) => input.access)
  const selectMine = createSelector(
    [
      selectAccessView,
      (input: AccessVmInput) => input.access.data,
      (input: AccessVmInput) => input.matrix.data,
      (input: AccessVmInput) => input.resources.data,
      (input: AccessVmInput) => input.forms,
      (input: AccessVmInput) => input.connectors ?? EMPTY_CONNECTORS,
    ],
    (query, access, matrix, resources, forms, connectors): MyAccessVm => {
      const api = access?.api ?? EMPTY
      const roles = access?.roles ?? EMPTY
      const superRole = roles.includes(SUPER_ROLE)
      const granted = new Set(api)
      return {
        query,
        roles: roles.map((role) => ({ value: role, label: roleTitle(role) })),
        superRole,
        noRoles: access !== undefined && roles.length === 0,
        total: api.length,
        groups: ownGroupsOf(api, forms),
        withheld:
          superRole && matrix !== undefined
            ? matrix.catalog.filter((entry) => !granted.has(entry.key)).map((entry) => ({ key: entry.key, title: titleOf(entry, forms) }))
            : EMPTY,
        gaps: gapsOf(resources, forms, connectors),
      }
    },
  )
  const selectSummary = createSelector(
    [selectMatrixView, (input: AccessVmInput) => input.matrix.data, (input: AccessVmInput) => input.forms],
    (query, matrix, forms): RolesSummaryVm => {
      const granted = new Map<string, ReadonlySet<string>>(
        (matrix?.roles ?? EMPTY).map((entry) => [entry.role, new Set(entry.permissions)]),
      )
      const has = (role: string, key: string) => role === SUPER_ROLE || (granted.get(role)?.has(key) ?? false)
      const groups = groupsOf(matrix, forms).map((group) => ({
        group: group.group,
        title: group.title,
        rows: group.items.map((item) => ({
          key: item.key,
          title: item.title,
          granted: PLATFORM_ROLES.map((role) => has(role.value, item.key)),
        })),
      }))
      return {
        query,
        roles: PLATFORM_ROLES,
        groups,
        totals: PLATFORM_ROLES.map((role) => groups.reduce((sum, group) => sum + group.rows.filter((row) => has(role.value, row.key)).length, 0)),
        catalogSize: matrix?.catalog.length ?? 0,
      }
    },
  )
  const selectSaveResources = savedOf((input) => input.saveResources)
  const selectSavePermissions = savedOf((input) => input.savePermissions)

  return createSelector(
    [
      selectSummary,
      selectMine,
      (input: AccessVmInput) => input.intent.role,
      selectManage,
      selectResourcesView,
      selectRows,
      selectGrantedView,
      (input: AccessVmInput) => input.granted.data,
      selectHidden,
      selectMatrixView,
      selectGroups,
      selectRolePermissions,
      selectSaveResources,
      selectSavePermissions,
      (input: AccessVmInput) => input.defaults?.data,
    ],
    (
      summary,
      mine,
      role,
      manage,
      resourcesView,
      rows,
      grantedView,
      grantedIds,
      hidden,
      matrixView,
      groups,
      rolePermissions,
      saveResources,
      savePermissions,
      defaults,
    ): AccessVm => ({
      summary,
      mine,
      manage,
      role,
      roleTitle: roleTitle(role),
      roles: PLATFORM_ROLES,
      superRole: role === SUPER_ROLE,
      resources: { query: resourcesView, rows },
      granted: { query: grantedView, ids: grantedIds ?? EMPTY, hidden },
      permissions: { query: matrixView, groups, granted: rolePermissions, defaults: role === SUPER_ROLE ? null : (defaults ?? null) },
      saveResources,
      savePermissions,
    }),
  )
}
